import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {Panel} from 'primeng/panel';
import {TableModule} from 'primeng/table';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {InputText} from 'primeng/inputtext';
import {Divider} from 'primeng/divider';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmationService} from 'primeng/api';
import {MessageService} from 'primeng/api';
import {DialogModule} from 'primeng/dialog';
import {ToastModule} from 'primeng/toast';
import { CommonEffects } from '../store/common.effects';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { selectLccaUnitData, selectLccaUnitTotal } from '../store/common.selector';
import { CatalogLccaUnitResponse } from '../../shared/models/common/lcca_unit.model';
import { selectIsLoadingAction } from '../../shared/store/global';
import { takeUntil } from 'rxjs/operators';
import * as action from '../store/common.actions';
import {toSignal} from '@angular/core/rxjs-interop';
import {BaseListComponent} from '../../shared/components/base-list.component';
import { AssetHistoryDialogComponent } from '../../shared/components/asset-history-dialog/asset-history-dialog.component';
import { ColumnFilters } from '../../shared/models/ui/table-column.model';

@Component({
  selector: 'lcca-unit-list',
  templateUrl: './lcca-unit-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Panel,
    TableModule,
    Button,
    IconField,
    InputIcon,
    InputText,
    Divider,
    ConfirmDialogModule,
    DialogModule,
    ToastModule,
    AssetHistoryDialogComponent
  ],
  providers: [ConfirmationService, MessageService, CommonEffects],
  styleUrls: ['./lcca-unit-list.component.scss']
})
export class LccaUnitListComponent extends BaseListComponent implements OnInit, OnDestroy {

  private messageService = inject(MessageService);
  private store$ = inject(Store<{ common: any }>)
  private actions$ = inject(Actions);

  lccaUnits = toSignal(this.store$.select(selectLccaUnitData), {initialValue: []});
  totalRecords = toSignal(this.store$.select(selectLccaUnitTotal), {initialValue: 0});
  loading = toSignal(this.store$.select(selectIsLoadingAction('[Common] Find LCCA Units')), {initialValue: false});

  
  readonly auditResourceName = 'Lcca Unit';
  historyVisible = false;
  historyRecordId: number | string | null = null;
  historyRecordLabel = '';

  selectedLccaUnits = signal<CatalogLccaUnitResponse[]>([]);

  // Add LCCA Unit Form Variables
  dialogVisible = signal(false);
  lccaUnitName = signal('');
  savingLccaUnit = signal(false);
  isEditMode = signal(false);
  editingLccaUnitId = signal<number | null>(null);

  deleteButtonLabel = computed(() => {
    return this.selectedLccaUnits().length > 1 ? 'Delete Multiple' : 'Delete';
  });

  override ngOnInit(): void {
    super.ngOnInit();

    // Success Alert
    this.actions$.pipe(
      ofType(action.createLccaUnitSuccess, action.updateLccaUnitSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingLccaUnit.set(false);
      this.closeDialog();
      this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
    })

    // Delete Success Alert
    this.actions$.pipe(
      ofType(action.deleteLccaUnitSuccess, action.bulkDeleteLccaUnitsSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.selectedLccaUnits.set([]);
      this.loadLccaUnits(); 
    })

    // Handle Error
    this.actions$.pipe(
      ofType(action.createLccaUnitError, action.updateLccaUnitError, action.deleteLccaUnitError, action.bulkDeleteLccaUnitsError),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingLccaUnit.set(false);
    })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  override loadData(page: number, size: number, filters: ColumnFilters): void {
    const keywords = filters['keywords'] || '';
    this.store$.dispatch(action.findLccaUnits({
      page,
      size,
      keywords
    }));
  }

  loadLccaUnits(): void {
    this.store$.dispatch(action.findLccaUnits({ page: 1, size: this.rows, keywords: ''}))
  }

  addNew(): void {
    this.lccaUnitName.set('');
    this.isEditMode.set(false);
    this.dialogVisible.set(true);
    this.editingLccaUnitId.set(null);
  }

  closeDialog(): void {
    this.lccaUnitName.set('');
    this.savingLccaUnit.set(false);
    this.isEditMode.set(false);
    this.dialogVisible.set(false);
    this.editingLccaUnitId.set(null);
  }

  saveLccaUnit(): void {
    if(!this.lccaUnitName().trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'LCCA unit name is required'
      });
      return;
    }

    this.savingLccaUnit.set(true);

    const lccaUnitData = {
      name: this.lccaUnitName().trim()
    };

    if(this.isEditMode() && this.editingLccaUnitId()) {
      this.store$.dispatch(action.updateLccaUnit({ id: this.editingLccaUnitId()!, lccaUnit: lccaUnitData }))
    } else {
      this.store$.dispatch(action.createLccaUnit({ lccaUnit: lccaUnitData }))
    }
  }

  editLccaUnit(): void {
    if (this.selectedLccaUnits().length === 1) {
      const lccaUnitToEdit = this.selectedLccaUnits()[0];
      this.lccaUnitName.set(lccaUnitToEdit.name ? lccaUnitToEdit.name : '');
      this.isEditMode.set(true);
      this.editingLccaUnitId.set(lccaUnitToEdit.id);
      this.dialogVisible.set(true);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select exactly one LCCA unit to edit'
      });
    }
  }

  deleteLccaUnit(): void {
    if (this.selectedLccaUnits().length === 0) {
      return;
    }

    const lccaUnitsToDelete = [...this.selectedLccaUnits()];
    const count = lccaUnitsToDelete.length;
    
    let message: string;
    if (count === 1) {
      message = `Are you sure you want to delete "${lccaUnitsToDelete[0].name}"?`;
    } else {
      const maxDisplay = 10;
      const namesToShow = lccaUnitsToDelete.slice(0, maxDisplay);
      const namesList = namesToShow.map((u, index) => `${index + 1}. ${u.name}`).join('<br>');
      
      let fullMessage = `Are you sure you want to delete ${count} LCCA units?<br><br><strong>Names to be deleted:</strong><br>${namesList}`;
      
      if (count > maxDisplay) {
        fullMessage += '<br>etc.';
      }
      
      message = fullMessage;
    }

    this.confirmationService.confirm({
      message: message,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (count === 1) {
          this.store$.dispatch(action.deleteLccaUnit({ id: lccaUnitsToDelete[0].id }));
        } else {
          const ids = lccaUnitsToDelete.map(u => u.id);
          this.store$.dispatch(action.bulkDeleteLccaUnits({ ids }));
        }
      }
    });
  }

  refreshLccaUnits(): void {
    this.selectedLccaUnits.set([]);
    this.clearFilter();
    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'LCCA units list has been refreshed'
    });
  }

  showHistory(): void {
    const selected = this.selectedLccaUnits();
    if (selected.length !== 1) {
      this.messageService.add({
        severity: 'info',
        summary: 'History',
        detail: 'Select one record to view history'
      });
      return;
    }
    const item = selected[0] as any;
    this.historyRecordId = item?.id ?? null;
    this.historyRecordLabel = item?.name || item?.unit_name || item?.code || String(this.historyRecordId ?? '');
    this.historyVisible = true;
  }
}

